/**
 * Weather windows at L2 (IDEAS X6). Each system carries a weather kind per sector day, drawn from its dominant
 * biome's mix with the same keyed, persistent draw the planets use on the ground (core/weather.ts), so a system's
 * sky on any day is reproducible from the sector's seed. A battle fought in a window carries the window's kind
 * down: `BattleSetup.weather` → `MatchSetup.weatherFixed`, and the match's planets hold that weather throughout
 * (a battle is minutes; a window is a day). The panel says what a window costs and gives; the stand-in sneaks into
 * one that hides. Behind `rules.weatherWindows`. Pure.
 */
import { WEATHER_EFFECTS, weatherAt, type WeatherKind } from '../core/weather';
import type { FactionId, SectorState, SystemId } from './types';
import type { Stance } from './types';

/** Sector days per weather window (one a day: a turn). */
export const WINDOW_DAYS = 1;
/** A window hides when the guards' perception falls to this or below (fog, storm, dust). */
export const HIDING_PERCEPTION = 0.6;

/** A system's weather on a day (the rule's own draw: keyed by the sector's seed and the system). */
export function systemWeatherAt(seed: string, systemId: SystemId, archetype: string, day: number): WeatherKind {
  return weatherAt(`${seed}-sector-${systemId}`, archetype, Math.floor(day / WINDOW_DAYS));
}

/** Write every system's weather for today into the state (the rule on). Idempotent for the same day. */
export function refreshWeather(state: SectorState): SectorState {
  if (!state.rules.weatherWindows) return state;
  const window = Math.floor(state.day / WINDOW_DAYS);
  if (state.weather && state.weather.window === window) return state;
  const kinds: Record<SystemId, WeatherKind> = {};
  for (const sys of state.systems) kinds[sys.id] = systemWeatherAt(state.seed, sys.id, sys.archetype, state.day);
  return { ...state, weather: { window, kinds } };
}

/** 62.19 (d): a system's weather for the next `days` days, today first (the rule on; null otherwise). */
export function systemForecast(state: Pick<SectorState, 'seed' | 'systems' | 'day' | 'rules'>, systemId: SystemId, days: number): WeatherKind[] | null {
  if (!state.rules.weatherWindows) return null;
  const sys = state.systems.find(s => s.id === systemId);
  if (!sys) return null;
  const first = Math.floor(state.day / WINDOW_DAYS);
  return Array.from({ length: days }, (_, i) => systemWeatherAt(state.seed, systemId, sys.archetype, (first + i) * WINDOW_DAYS));
}

/** A forecast as one line: "fog · rain · clear · clear" with hiding windows marked. */
export function forecastText(kinds: readonly WeatherKind[]): string {
  return kinds.map(k => (windowHides(k) ? `${k}*` : k)).join(' · ');
}

/** The window a system is in now, or null (the rule off, or not yet refreshed). */
export function systemWeather(state: Pick<SectorState, 'weather' | 'rules'>, systemId: SystemId): WeatherKind | null {
  if (!state.rules.weatherWindows) return null;
  return state.weather?.kinds[systemId] ?? null;
}

/** Does this window hide an approach (the guards see short)? */
export function windowHides(kind: WeatherKind | null): boolean {
  return kind !== null && WEATHER_EFFECTS[kind].perception <= HIDING_PERCEPTION;
}

/** What a window costs and gives, for the panel: one line. */
export function windowText(kind: WeatherKind): string {
  const e = WEATHER_EFFECTS[kind];
  if (kind === 'clear') return 'clear — the horizon decides; nothing hides, nothing is missed';
  const sight = e.sight === Infinity ? 'to the horizon' : `${e.sight} lengths`;
  const gives = windowHides(kind) ? 'a sneak closes unseen, guards see short' : 'guards see a little less';
  const costs = e.hearing < 1 ? `guns see ${sight} and hear ×${e.hearing}` : `guns see ${sight}`;
  return `${kind} — gives: ${gives}; costs: ${costs}`;
}

/** The stand-in: in a window that hides, approach a garrisoned enemy system by sneaking. */
export function stanceInWindow(state: SectorState, faction: FactionId, target: SystemId, fallback: Stance): Stance {
  const sys = state.systems.find(s => s.id === target);
  if (!sys || !sys.owner || sys.owner === faction || !sys.garrisoned) return fallback;
  return windowHides(systemWeather(state, target)) ? 'sneak' : fallback;
}
